import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { Policy } from '../customer/Policy';
import { Product } from '../static/Product';
import { GetOwnerInformationSuccess, PAGovtIdTypes, PolicyAdminClient } from './PolicyAdminClient';
import { PolicyAdminPolicy } from './PolicyAdminClient';
import { PolicyAdminStatus } from './PolicyAdminStatus';

import requestPromise from 'request-promise-native';
import _ from 'lodash';
import { PolicyAdminClient as PAGQLClient } from '@hit/policy-admin-client';
import { HitAppCtx } from '../orca/HitAppCtx';
import { GqlTypes } from '@hit/policy-admin-types';
import { Utils } from '../Utils';
import { serverOnly } from '../helpers';
import { PolicyInfoPayload, ProductInfoPayload } from '@hit/policy-admin-types/dist/data-model/Types';
import { PolicyAdminRiders, CombinedSegmentDTO, PolicyValuesDTO, BasicPolicyInfoDTO } from './PolicyAdminRiders';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';
import { CoverpathUtils } from '../../../coverpath-common/js/static/CoverpathUtils';

@supertypeClass({ toClient: false, toServer: false })
export class PolicyAdminUtils {
  // -------------------------------------
  // Accessible Utilities
  public static async getPolicyAdminStatus(
    policy: Policy,
    existingPolicyAdminPolicy?: PolicyAdminPolicy
  ): Promise<PolicyAdminStatus> {
    let policyAdminPolicy = null;

    if (PolicyAdminUtils.couldPolicyAdminHavePolicy(policy)) {
      policyAdminPolicy = existingPolicyAdminPolicy || (await PolicyAdminClient.getPolicyAdminPolicy(policy));
    } else {
      UILoggerSingleton.getInstance().info(
        {
          module: 'PolicyAdminClient',
          function: 'policyAdminApiGet',
          category: UICategory.request,
          data: {
            policyNumber: policy.policyNumber,
            tlicEffectiveDate: policy.tLICEffectiveDate,
            effectiveDate: policy.effectiveDate,
          },
        },
        'Skipping checking status from Policy Admin'
      );

      policyAdminPolicy = PolicyAdminUtils.generateWorkflowPolicyStatus();
    }

    let policyAdminStatus: PolicyAdminStatus = null;
    if (policyAdminPolicy) {
      policyAdminStatus = PolicyAdminStatus.new(policyAdminPolicy);
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminClient',
        function: 'policyAdminApiGet',
        category: UICategory.request,
        data: {
          policyNumber: policy.policyNumber,
          tlicEffectiveDate: policy.tLICEffectiveDate,
          effectiveDate: policy.effectiveDate,
          faceAmount: policyAdminStatus ? policyAdminStatus.getFaceAmount() : null,
          duration: policyAdminStatus ? policyAdminStatus.getDuration() : null,
          freelookDate: policyAdminStatus ? policyAdminStatus.getFreeLookEndDate() : null,
          isPAPolicy: policyAdminStatus ? policyAdminStatus.isPAPolicy() : false,
        },
      },
      'Acquired PolicyAdminStatus'
    );

    return policyAdminStatus;
  }

  public static async getPolicyAdminPremium(policy: Policy) {
    if (PolicyAdminUtils.couldPolicyAdminHavePolicy(policy)) {
      return PolicyAdminClient.getPolicyPremiumBreakdown(policy);
    }
    return null;
  }

  public static async getPolicyAdminStatusBatch(
    policyNumberArray: Array<string>,
    channel: string
  ): Promise<Map<string, PolicyAdminStatus>> {
    const policyAdminPolicies: Array<PolicyAdminPolicy> | undefined = await PolicyAdminClient.getPolicyAdminPolicyBatch(
      policyNumberArray,
      channel
    );
    const policyAdminStatusMap: Map<string, PolicyAdminStatus> = new Map<string, PolicyAdminStatus>();
    if (!policyAdminPolicies) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'PolicyAdminUtils',
          function: 'getPolicyAdminStatusBatch',
          category: UICategory.request,
        },
        `No results returned from policy admin API for policy numbers [${policyNumberArray.join(', ')}]`
      );
      return policyAdminStatusMap;
    }

    for (const policyAdminPolicy of policyAdminPolicies) {
      UILoggerSingleton.getInstance().info({
        module: 'PolicyAdminUtils',
        function: 'getPolicyAdminStatusBatch 1 ',
        category: UICategory.request,
        data: {
          status: policyAdminPolicy.policyInfo.status,
          statusCode: policyAdminPolicy.policyInfo.statusCode,
          rateClass: policyAdminPolicy.rateClass,
        },
      });
      const policyAdminStatus = PolicyAdminStatus.new(policyAdminPolicy);
      policyAdminStatusMap.set(policyAdminPolicy.policyInfo.policyNumber, policyAdminStatus);
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'PolicyAdminUtils',
        function: 'getPolicyAdminStatusBatch',
        category: UICategory.request,
      },
      `Acquired PolicyAdminStatus for policy numbers: [${Array.from(policyAdminStatusMap.keys()).join(', ')}]`
    );

    return policyAdminStatusMap;
  }

  public static async getPolicyRidersInfo(policy: Policy): Promise<PolicyAdminRiders> {
    const calls: [
      Promise<CombinedSegmentDTO>,
      Promise<ProductInfoPayload>,
      Promise<PolicyValuesDTO>,
      Promise<BasicPolicyInfoDTO>
    ] = [
      PolicyAdminClient.getInsuranceSegmentInfo(policy),
      PolicyAdminClient.getProductInfo(policy),
      PolicyAdminClient.getPolicyValues(policy),
      PolicyAdminClient.getBasicPolicyInfo(policy),
    ];

    return Promise.all(calls).then((responses) => {
      const segmentInfo = responses[0];
      const productInfo = responses[1];
      const policyValues = responses[2];
      const basicPolicyInfo = responses[3];
      if (!segmentInfo) {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'PolicyAdminUtils',
            function: 'getPolicyRidersInfo',
            category: UICategory.request,
          },
          `No results returned from policy admin API for policy numbers ${policy.policyNumber}`
        );
        return undefined;
      }
      const paRiders = PolicyAdminRiders.new(segmentInfo);

      if (productInfo) {
        paRiders.setProductInfo(productInfo);
      }
      if (policyValues) {
        paRiders.setPolicyValues(policyValues);
      }
      if (basicPolicyInfo) {
        paRiders.setBasicPolicyInfo(basicPolicyInfo);
      }
      UILoggerSingleton.getInstance().info(
        {
          module: 'PolicyAdminUtils',
          function: 'getPolicyRidersInfo',
          category: UICategory.request,
        },
        `Acquired PolicyAdminRiders for policy number: ${policy.policyNumber}`
      );
      return paRiders;
    });
  }

  /**
   * Fetch annuity status from PA using their API
   *
   * @param   {AnnuitySummaryInfo}  annuity
   *
   * @return  {Promise<string>}     Policy status if present, undefined otherwise
   */
  @serverOnly()
  public static async getPolicyAdminAnnuityStatus(policyNumber: string): Promise<string> {
    const requestId = Utils.generateUuid();
    const paClient = PAGQLClient.new({
      config: {
        policyAdminURL: PolicyAdminClient.getPaClientUrl(),
        AUTH_TOKEN: amorphicStatic.config.nconf.get('cpApiGatewayJWTToken'),
      },
      exnCtx: {
        msgCtx: {
          requestId: requestId,
          userId: '123',
        },
      },
    });

    const request: GqlTypes.GetAnnuityPolicyPayload = {
      policyNumber: policyNumber,
    };

    try {
      const annuityStatus = await paClient.getAnnuityPolicy(request, {
        status: null,
      });

      const status: string = _.get(annuityStatus, 'data.getAnnuityPolicy.status', undefined);

      if (status) {
        // PA returns the status in the following format: "<Status> (<Workflow state> <Status>)"
        // We split the response on the opening parenthesis and return only the status
        return status.split('(')[0].trim();
      }
    } catch (e) {
      UILoggerSingleton.getInstance().error({
        module: 'PolicyAdminUtils',
        function: 'getPolicyAdminAnnuityStatus',
        category: UICategory.request,
        error: {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
          isHumanRelated: false,
          data: {
            requestId,
            policyNumber: policyNumber,
          },
        },
      });
    }
  }

  /**
   * Fetch PolicyInfo from PA using their API
   *
   * @param   {policyNumber}  Policy
   *
   * @return  {Promise<Array<PolicyInfoPayload>>}
   */
  @serverOnly()
  public static async getPAPolicyInfoBatch(policyNumbers: Array<string>): Promise<Array<PolicyInfoPayload>> {
    const requestId = Utils.generateUuid();
    const paClient = PAGQLClient.new({
      config: {
        policyAdminURL: PolicyAdminClient.getPaClientUrl(),
        AUTH_TOKEN: amorphicStatic.config.nconf.get('cpApiGatewayJWTToken'),
      },
      exnCtx: {
        msgCtx: {
          requestId: requestId,
          userId: '123',
        },
      },
    });

    try {
      const response = await paClient.getPolicyInfoBatch(policyNumbers);
      const payload = JSON.parse(response).payload;
      if (payload) {
        return payload;
      }
    } catch (e) {
      LoggerSingleton.getInstance().error({
        module: 'PolicyAdminUtils',
        function: 'getPAPolicyInfoBatch',
        category: Category.request,
        error: {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
          isHumanRelated: false,
          data: {
            requestId,
            policyNumber: policyNumbers,
          },
        },
      });
    }
  }

  public static async hasPolicyBeenSentToCsc(policy: Policy): Promise<boolean> {
    return Promise.resolve(false);
  }

  public static async hasPolicyBeenSentToPolicyAdmin(policy: Policy): Promise<boolean> {
    return Promise.resolve(true);
  }

  private static cleanGetRequest(body, policyNumber: string): void {
    body.context.referenceId = policyNumber;
    body.payload.policyNumber = policyNumber;
  }

  @serverOnly()
  public static updatePolicyInfoInPolicyAdmin(
    path: string,
    policy: Policy,
    body,
    policyAdminPolicyId: string
  ): Promise<void> {
    this.cleanUpdateRequest(body, policy.policyNumber, policyAdminPolicyId);
    const policyAdminUrl = PolicyAdminClient.assemblePolicyAdminUrl(policy.getChannelCode(), path);

    const options = {
      uri: policyAdminUrl,
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: body,
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };

    return requestPromise(options)
      .then((response) => {
        // return response && response.payload;
      })
      .catch((error) => {
        LoggerSingleton.getInstance().error({
          module: 'PolicyAdminUtils',
          function: 'updatePolicyInfoInPolicyAdmin',
          category: Category.request,
          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              policyNumber: policy.policyNumber,
            },
          },
        });
        return error;
      });
  }

  private static cleanUpdateRequest(body, policyNumber: string, policyAdminPolicyId: string): void {
    body.payload.policyId = policyAdminPolicyId;
    body.payload.policyNumber = policyNumber;
  }

  public static couldPolicyAdminHavePolicy(policy: Policy, skipWfHistory?: boolean): boolean {
    if (policy && policy.policyNumber) {
      if (!!policy.tLICEffectiveDate) {
        return true;
      } else if (CoverpathUtils.isSanderlingPolicy(policy.product)) {
        return true;
      } else if (!!policy.freeLookEndsOn) {
        if (skipWfHistory) {
          return true;
        } else if (policy.workflowStateHistory.length) {
          return policy.workflowStateHistory.some((state) => state.subState === 'Issuing Policy');
        }
      }
    }
    return false;
  }

  @serverOnly()
  public static async getPolicyInfo(policyNumber: string, requestId: string) {
    const paClient = PAGQLClient.new({
      config: {
        policyAdminURL: PolicyAdminClient.getPaClientUrl(),
        AUTH_TOKEN: amorphicStatic.config.nconf.get('cpApiGatewayJWTToken'),
      },
      exnCtx: {
        msgCtx: {
          requestId: requestId,
          userId: '123',
        },
      },
    });
    return paClient.getPolicyInfo(policyNumber);
  }

  // -------------------------------------
  // Private Utilities

  private static generateWorkflowPolicyStatus() {
    const workflowPolicy = new PolicyAdminPolicy();
    workflowPolicy.isPolicyAdminPolicy = false;

    return workflowPolicy;
  }

  @serverOnly()
  public static async getOwnerIdFromPA(policy: Policy, email: string): Promise<string | undefined> {
    const data = {
      module: 'PolicyAdminUtils',
      function: 'getOwnerIdFromPA',
      category: Category.request,
      data: {
        policyNumber: policy.policyNumber,
      },
    };

    LoggerSingleton.getInstance().info(data, `Checking if policy exists in PA`);
    let ownerId, res;
    try {
      res = await this.getOwnerInformation(policy);
      if (res) {
        ownerId = this.filterOwnerIdFromPAresponse(res, policy, email);
      }
    } catch (err) {
      throw new Error(err);
    }

    if (ownerId) {
      LoggerSingleton.getInstance().info(data, `Policy exists in PA`);
      return ownerId;
    } else {
      LoggerSingleton.getInstance().info(data, `Policy does not exist in PA`);
      return undefined;
    }
  }

  @serverOnly()
  public static async getOwnerInformation(policy: Policy): Promise<GetOwnerInformationSuccess | string> {
    const data = {
      module: 'PolicyAdminUtils',
      function: 'getOwnerInformation',
      category: Category.request,
      data: {
        policyNumber: policy.policyNumber,
      },
    };

    // No policy number so it definitely is not in Policy Admin
    if (!policy.policyNumber) {
      LoggerSingleton.getInstance().info(
        data,
        `Policy does not have a policy number and thus will not be in PA. Skipping PA get owner info call.`
      );
      return undefined;
    }

    LoggerSingleton.getInstance().info(data, `Sending a request to policy admin to get owner information`);

    const payload = PolicyAdminClient.buildGetOwnerInformationRequest(policy);

    const policyAdminUrl = PolicyAdminClient.assemblePolicyAdminUrl(
      policy.getChannelCode(),
      PolicyAdminClient.END_POINT_PATHS.GET_OWNERS
    );

    const options = {
      uri: policyAdminUrl,
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: payload,
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };

    try {
      let res = await requestPromise(options);
      LoggerSingleton.getInstance().info(data, `Successfully sent request to policy admin to get owner information`);

      // PA Returns Errors as a string (with 200 OK)
      if (typeof res === 'string') {
        LoggerSingleton.getInstance().info(data, `PA response from failed get: ${res}`);
        res = undefined;
      }

      return res;
    } catch (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'PolicyAdminUtils',
          function: 'getOwnerInformation',
          category: Category.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              policyNumber: policy.policyNumber,
            },
          },
        },
        `Failure to retrieve owner information from policy admin`
      );
      throw err;
    }
  }

  @serverOnly()
  public static async updateOwnerEmail(email: string, policy: Policy, ownerId: string): Promise<OwnerUpdateResponse> {
    const data = {
      module: 'PolicyAdminUtils',
      function: 'updateOwnerEmail',
      category: Category.request,
      data: {
        policyNumber: policy.policyNumber,
      },
    };
    LoggerSingleton.getInstance().info(data, `Sending a request to policy admin to update owner email`);

    const payload = PolicyAdminClient.buildUpdateOwnerEmailRequest(email, policy, ownerId);

    const policyAdminUrl = PolicyAdminClient.assemblePolicyAdminUrl(
      policy.getChannelCode(),
      PolicyAdminClient.END_POINT_PATHS.UPDATE_OWNER_EMAIL
    );
    const options = {
      uri: policyAdminUrl,
      method: PolicyAdminClient.REQUEST_TYPES.POST,
      json: true,
      body: payload,
      timeout: PolicyAdminClient.DEFAULT_TIMEOUT,
      headers: PolicyAdminClient.getPolicyAdminApiHeader(),
    };

    try {
      const res = await requestPromise(options);

      // Unable to issue update owner request on Policy Admin - throw error and immediately catch
      if (typeof res === 'string') {
        throw new Error(res);
      }

      LoggerSingleton.getInstance().info(data, `Successfully sent request to policy admin to update owner email`);
      return OwnerUpdateResponse.UpdatedSuccessfully;
    } catch (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'PolicyAdminUtils',
          function: 'updateOwnerEmail',
          category: Category.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              policyNumber: policy.policyNumber,
            },
          },
        },
        `Failure to update owner email in policy admin`
      );
      return OwnerUpdateResponse.ErrorInPACall;
    }
  }

  @serverOnly()
  private static filterOwnerIdFromPAresponse(
    res: GetOwnerInformationSuccess | string,
    policy: Policy,
    email: string
  ): string | undefined {
    let ownerId: string;

    const cpOwner = policy.owner as any;

    const data = {
      module: 'PolicyAdminUtils',
      function: 'filterOwnerIdFromPAresponse',
      category: Category.request,
      data: {
        policyNumber: policy.policyNumber,
        res: undefined,
      },
    };

    let cpGovtId: string;
    let cpGovtIdType: string;

    /** From haven/apps/hitDataService/js/ServicesUtil.ts
     *  * CASE WHEN pa."SSN" IS NOT NULL THEN pa."SSN"
     WHEN pa."taxId" IS NOT NULL THEN pa."taxId"
     ELSE pa."TIN" END AS "govtId",
     CASE WHEN pa."SSN" IS NOT NULL THEN 'ssn'
     WHEN pa."taxId" IS NOT NULL THEN 'ein'
     WHEN pa."TIN" IS NOT NULL THEN 'itin'
     ELSE NULL END AS "govtIdType"
     *
     */
    if (cpOwner.SSN) {
      cpGovtId = cpOwner.SSN;
      cpGovtIdType = PAGovtIdTypes.ssn;
    } else if (cpOwner.taxId) {
      cpGovtId = cpOwner.taxId;
      cpGovtIdType = PAGovtIdTypes.ein;
    } else if (cpOwner.TIN) {
      cpGovtId = cpOwner.TIN;
      cpGovtIdType = PAGovtIdTypes.itin;
    } else if (cpOwner.EIN) {
      cpGovtId = cpOwner.EIN;
      cpGovtIdType = PAGovtIdTypes.ein;
    } else {
      LoggerSingleton.getInstance().info(data, `No owner government id found in HIT DB`);

      return undefined;
    }

    cpGovtId = this.cleanGovtId(cpGovtId);
    cpGovtIdType = cpGovtIdType.toLowerCase();

    if (Array.isArray(res) && res.length > 0) {
      if (res.length === 1) {
        if (res[0] && res[0].contactInfo && !Utils.equalsIgnoreCase(res[0].contactInfo.email, email)) {
          ownerId = res[0].id;
        }
      } else {
        const match = res.find((owner) => {
          let paGovtId, paGovtIdType;

          if (owner && owner.contactInfo && Utils.equalsIgnoreCase(owner.contactInfo.email, email)) {
            return false;
          }

          if (owner && owner.party && owner.party.person) {
            paGovtId = this.cleanGovtId(owner.party.person.govtId);
            paGovtIdType = owner.party.person.govtIdType.toLowerCase();
          } else if (owner && owner.party && owner.party.entity) {
            paGovtId = this.cleanGovtId(owner.party.entity.govtId);
            paGovtIdType = owner.party.entity.govtIdType.toLowerCase();
          } else {
            return false;
          }

          return paGovtId === cpGovtId && paGovtIdType === cpGovtIdType;
        });
        if (match) {
          ownerId = match.id;
        }
      }
    }

    if (ownerId) {
      LoggerSingleton.getInstance().info(data, `Found owner from Policy Admin`);

      return ownerId;
    } else {
      if (typeof res === 'string') {
        data.data.res = res;
      }
      LoggerSingleton.getInstance().info(
        data,
        `No owner id found for this user in PA, or email matched and will not update`
      );
      return undefined;
    }
  }

  @serverOnly()
  private static cleanGovtId(govtId: string) {
    return govtId.trim().replace(/-/g, '');
  }
}

export enum OwnerUpdateResponse {
  UpdatedSuccessfully,
  NonExistentInPAOrUpdateNotNeeded,
  ErrorInPACall,
}

export type GetOwnerResponse = {
  ownerId: string;
};
